import Immutable from 'seamless-immutable';

import {
  FACET_URL_PARAMS,
  FACET_URL_PATH,
  PAGE_TYPE_CHANGE,
  RECEIVE_SEARCH_RESPONSE,
  REQUEST_SEARCH,
  RESET_FACET_GROUP,
  TOGGLE_FACETS,
  TOGGLE_SELECTED_FACET,
  TOGGLE_SELECTED_FACET_GROUP
} from 'constants/reduxActions';
import { FILTERS_RE } from 'common/regex';
import { getResponseNavigation } from 'helpers/SearchUtils';
import marketplace from 'cfg/marketplace.json';

const {
  search: { topBrands }
} = marketplace;

const initialState = {
  requestedUrl: null,
  navigation: [],
  facetUrlQueryParams: null,
  facetUrlPath: null,
  shouldCollapseOnNextResponse: false
};

export function isFacetsVisible(state = false, action) {
  const { type, visible, pageType } = action;
  switch (type) {
    case TOGGLE_FACETS:
      return visible;
    case PAGE_TYPE_CHANGE:
      return pageType === 'search' ? state : false;
    default:
      return state;
  }
}

export const sortTopBrands = (facets, orderedBrands = topBrands) => {
  const facetValuesCopy = [...facets.values];
  const foundTopValues = facetValuesCopy
    .filter((v, i) => {
      const foundIndex = orderedBrands.indexOf(v.name.toLowerCase()) > -1;
      if (foundIndex) {
        facetValuesCopy.splice(i, 1);
        return foundIndex;
      }
    })
    .sort((a, b) => orderedBrands.indexOf(a.name) - orderedBrands.indexOf(b.name));
  return foundTopValues.concat(facetValuesCopy);
};

/**
 * Process individual facet values — handles facetUrl/facetZsoUrl conversion and displayName.
 */
const processValues = values =>
  values.map(facetValue => {
    const newValue = { ...facetValue };

    // Handle facetUrl vs facetZsoUrl
    if (FILTERS_RE.test(facetValue.facetUrl)) {
      newValue.facetZsoUrl = facetValue.facetUrl;
      newValue.facetUrl = null;
    }

    // Add displayName if missing
    if (!facetValue.displayName) {
      newValue.displayName = facetValue.name;
    }

    return newValue;
  });

/**
 * Transforms facets that have an embedded `subGroups` array into the same
 * FacetGroupType structure produced by groupConsecutiveFacets.
 *
 * This handles the case where a single API entry contains multiple sub-facets
 * (e.g. kids' shoe sizes with Infant/Toddler/Little Kid sub-groups) that
 * cannot be split into separate facetField entries on the backend due to limitations
 * on `facetField` needing to be unique in each object.
 *
 * Runs BEFORE groupConsecutiveFacets so that both paths produce the same output type.
 */
export const expandSubGroupFacets = navigationData =>
  navigationData.map(facet => {
    if (!facet.subGroups || facet.subGroups.length === 0) {
      return facet;
    }

    const children = facet.subGroups.map(subGroup => ({
      type: 'single',
      facetField: facet.facetField,
      facetFieldDisplayName: subGroup.displayName,
      facetUrl: facet.facetUrl,
      symbolicSizingField: facet.symbolicSizingField ?? false,
      multiSelect: facet.multiSelect,
      groupId: facet.groupId,
      groupDisplayName: facet.groupDisplayName,
      quickFilter: facet.quickFilter,
      parentGroupId: facet.parentGroupId,
      parentGroupDisplayName: facet.parentGroupDisplayName,
      quickFilterGroupId: facet.quickFilterGroupId,
      quickFilterGroupDisplayName: facet.quickFilterGroupDisplayName,
      quickFilterParentGroupId: facet.quickFilterParentGroupId,
      quickFilterParentGroupDisplayName: facet.quickFilterParentGroupDisplayName,
      values: subGroup.values
    }));

    return {
      type: 'group',
      facetField: facet.groupId,
      facetFieldDisplayName: facet.groupDisplayName,
      facetUrl: facet.facetUrl,
      groupId: facet.groupId,
      groupDisplayName: facet.groupDisplayName,
      children,
      values: []
    };
  });

/**
 * Groups consecutive facets that share the same groupId into a single
 * navigation entry. The first facet in a group becomes the top-level entry
 * (using groupDisplayName as the accordion heading), and all consecutive
 * facets with the same groupId become its `children`.
 *
 * Facets without a groupId, or whose groupId doesn't match their neighbor,
 * pass through ungrouped.
 */
export const groupConsecutiveFacets = navigationData => {
  const grouped = [];

  // An item may already be a group (e.g. produced by expandSubGroupFacets). When
  // merging it into a sibling group we want its children, not the group wrapper —
  // otherwise we end up with a group nested inside a group.
  const asChildren = item => (item.type === 'group' ? item.children : [item]);

  for (const current of navigationData) {
    const lastOutput = grouped[grouped.length - 1];
    const belongsToSameGroup = current.groupId && lastOutput?.groupId === current.groupId;

    if (!belongsToSameGroup) {
      grouped.push(current);
    } else if (lastOutput.type !== 'group') {
      // 2nd item — convert the standalone into a group
      grouped[grouped.length - 1] = {
        type: 'group',
        facetField: lastOutput.groupId,
        facetFieldDisplayName: lastOutput.groupDisplayName || lastOutput.facetFieldDisplayName,
        facetUrl: lastOutput.facetUrl,
        groupId: lastOutput.groupId,
        groupDisplayName: lastOutput.groupDisplayName,
        children: [...asChildren(lastOutput), ...asChildren(current)],
        values: []
      };
    } else {
      // 3rd+ item — append to existing group
      lastOutput.children.push(...asChildren(current));
    }
  }

  return grouped;
};

export default function facets(state = initialState, action) {
  const { type, isFresh, selectedFacetGroupIndex, selectedFacetGroupName, selectedFacetIndex, response, url, facetUrlPath, facetUrlQueryParams } =
    action;
  let newState = Immutable(state);

  switch (type) {
    case TOGGLE_SELECTED_FACET_GROUP: {
      const facetIndex = newState.navigation.findIndex(facet => facet.facetField === selectedFacetGroupName);

      if (facetIndex > -1) {
        newState = newState.updateIn(['navigation', facetIndex, 'isExpanded'], v => !v);
      }

      return newState;
    }

    case TOGGLE_SELECTED_FACET: {
      const facet = newState.navigation[selectedFacetGroupIndex];

      if (facet) {
        if (facet.multiSelect === false) {
          // Single-select facets: clear other selections and set the new one
          newState = newState.updateIn(['navigation', selectedFacetGroupIndex, 'values'], values =>
            values.map((value, i) => ({ ...value, selected: i === selectedFacetIndex }))
          );
        } else {
          // Multi-select facets: toggle the selected state
          newState = newState.updateIn(['navigation', selectedFacetGroupIndex, 'values', selectedFacetIndex, 'selected'], v => !v);
        }
      }

      return newState;
    }

    case REQUEST_SEARCH:
      if (isFresh) {
        newState = Immutable(initialState);
      }
      return newState.set('requestedUrl', url);

    case RECEIVE_SEARCH_RESPONSE: {
      // TODO: Remove V2P3 fallback once V2P3 is fully rolled out. https://taskei.amazon.dev/tasks/FEE-7956
      // TODO: Remove V2P3/V2P2 fallback chain once V2P3 is fully rolled out. https://taskei.amazon.dev/tasks/FEE-7956
      const isV2P3 = !!response.navigationV2P3;
      const navigationData = getResponseNavigation(response);

      if (response.url === state.requestedUrl && navigationData) {
        const shouldCollapse = state.shouldCollapseOnNextResponse;

        // For V2P3, expand inline subGroups then group consecutive facets that share the same groupId
        const groupedNavigation = isV2P3 ? groupConsecutiveFacets(expandSubGroupFacets(navigationData)) : navigationData;

        // Process navigation flat array structure
        const navigationWithState = groupedNavigation.map(facetGroup => {
          const newFacetGroup = { ...facetGroup };
          const oldFacetGroup = state.navigation?.find(({ facetField }) => facetField === facetGroup.facetField);

          // Preserve UI state OR collapse if flag is set
          newFacetGroup.isExpanded = shouldCollapse ? false : oldFacetGroup ? oldFacetGroup.isExpanded : false;

          // For grouped facets, process each child's values
          if (newFacetGroup.type === 'group') {
            newFacetGroup.children = newFacetGroup.children.map(child => {
              const processedChild = { ...child, type: 'single' };
              if (processedChild.values) {
                processedChild.values = processValues(processedChild.values);
              }
              return processedChild;
            });
          } else {
            // Stamp type and process facet values for non-grouped facets
            newFacetGroup.type = 'single';
            if (newFacetGroup.values) {
              newFacetGroup.values = processValues(newFacetGroup.values);

              // Apply top brands sorting for brand facets
              if (topBrands.length > 0 && facetGroup.facetField === 'brandNameFacet') {
                newFacetGroup.values = sortTopBrands({ values: newFacetGroup.values });
              }
            }
          }

          return newFacetGroup;
        });

        newState = newState.set('navigation', navigationWithState);
        newState = newState.set('shouldCollapseOnNextResponse', false);
      }

      return newState;
    }

    case FACET_URL_PATH:
      newState = newState.set('facetUrlPath', facetUrlPath);
      return newState;

    case FACET_URL_PARAMS:
      newState = newState.set('facetUrlQueryParams', facetUrlQueryParams);
      return newState;

    case RESET_FACET_GROUP:
      // Don't deselect values immediately - let RECEIVE_SEARCH_RESPONSE handle it
      // Just set flag to collapse and clear selections when response arrives
      newState = newState.set('shouldCollapseOnNextResponse', true);

      return newState;

    default:
      return state;
  }
}
